import { type CybusServiceDeviation, type DeviationEventToastItem, selectDeviationCounterToast, selectDeviationEventToasts } from '../../../domain'

import { Usecase } from '../Usecase'

const EVENT_TOAST_MAX = 3

/**
 * Feeds the deviation counter toast + event cascade from the two data sources
 * `DeviationNotifications/Notifications.tsx` subscribes to: a periodic snapshot of currently-deviated
 * services (`syncDeviations`) and a one-at-a-time resolution event feed (`notifyResolved`). Kept
 * separate from `ToastUsecase`: an unaggregated, individually-timed event cascade plus an ongoing
 * running total don't fit that usecase's dedupe/severity-cap model, and render in their own zone.
 */
export class DeviationToastsUsecase extends Usecase {
    private nextEventId = 0

    /** Ids seen in the most recent `syncDeviations` snapshot. `undefined` until the first call. */
    private seenIds: ReadonlySet<CybusServiceDeviation['id']> | undefined

    /**
     * The total the counter toast was last set to show, kept even after `dismissCounter()` clears the
     * live toast - so a later snapshot reporting that same total (a poll redelivery, or the backend
     * simply not having changed yet) doesn't resurrect a toast the user just closed. Only a genuinely
     * different total re-shows it.
     */
    private lastCounterCount: number | null = null

    private static appendEventToast (queue: ReadonlyArray<DeviationEventToastItem>, item: DeviationEventToastItem): ReadonlyArray<DeviationEventToastItem> {
        const appended = [...queue, item]
        return appended.length > EVENT_TOAST_MAX ? appended.slice(appended.length - EVENT_TOAST_MAX) : appended
    }

    /** Only writes when the count actually changed - a redelivered-but-unchanged snapshot shouldn't reset the renderer's dismiss timer, or resurrect a toast the user already dismissed at that same total. */
    private refreshCounter (totalCount: number): void {
        if (totalCount <= 0) {
            this.lastCounterCount = null

            if (selectDeviationCounterToast(this.getState()) !== null) {
                this.setState({ deviationCounterToast: null })
            }
        } else if (totalCount !== this.lastCounterCount) {
            this.lastCounterCount = totalCount
            this.setState({ deviationCounterToast: { count: totalCount } })
        }
    }

    /**
     * The first call only seeds the "already seen" baseline, so pre-existing deviations at
     * login/mount don't each fire a "new" event toast. Later calls emit one per newly-appeared id.
     */
    syncDeviations (current: ReadonlyArray<Pick<CybusServiceDeviation, 'id'>>, totalCount: number): void {
        const currentIds = new Set(current.map(({ id }) => id))
        const previousIds = this.seenIds
        this.seenIds = currentIds

        if (previousIds === undefined) {
            return
        }

        const newIds = current.filter(({ id }) => !previousIds.has(id))

        if (newIds.length > 0) {
            let queue = selectDeviationEventToasts(this.getState())

            for (const { id: serviceId } of newIds) {
                queue = DeviationToastsUsecase.appendEventToast(queue, { id: `deviation-event-${this.nextEventId++}`, kind: 'new', serviceId })
            }

            this.setState({ deviationEventToasts: queue })
        }

        this.refreshCounter(totalCount)
    }

    /** Reacts to a single resolution event (`serviceDeviationResolution`). */
    notifyResolved (resolution: CybusServiceDeviation, totalCount: number): void {
        const queue = DeviationToastsUsecase.appendEventToast(selectDeviationEventToasts(this.getState()), {
            id: `deviation-event-${this.nextEventId++}`,
            kind: 'resolved',
            serviceId: resolution.id,
        })

        this.setState({ deviationEventToasts: queue })
        this.refreshCounter(totalCount)
    }

    dismissEvent (id: DeviationEventToastItem['id']): void {
        this.setState({ deviationEventToasts: selectDeviationEventToasts(this.getState()).filter((item) => item.id !== id) })
    }

    dismissCounter (): void {
        if (selectDeviationCounterToast(this.getState()) !== null) {
            this.setState({ deviationCounterToast: null })
        }
    }
}
